import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircleIcon, PlusIcon, MailIcon } from 'lucide-react';
type Category = 'Compra' | 'Producto' | 'Uso' | 'Garantía';
const faqs: {
  q: string;
  a: string;
  category: Category;
}[] = [{
  q: '¿Cómo recibo el material?',
  a: 'Inmediatamente en tu email tras el pago. Recibirás un enlace de descarga con todos los archivos listos para usar.',
  category: 'Compra'
}, {
  q: '¿Sirve para clases particulares?',
  a: 'Sí, especialmente el módulo Business Pro te ayuda a rentabilizarlas y atraer más alumnos privados.',
  category: 'Producto'
}, {
  q: '¿Incluye juegos?',
  a: 'Sí, un bono completo de juegos y dinámicas pensados para todos los niveles y edades.',
  category: 'Producto'
}, {
  q: '¿Sirve para matronatación?',
  a: 'Se enfoca desde ambientación (3-4 años) hasta adultos máster, cubriendo todas las etapas del aprendizaje.',
  category: 'Producto'
}, {
  q: '¿Hay algún costo recurrente o suscripción?',
  a: 'No. Pagas una sola vez US$ 19.90 y obtienes acceso permanente a todo el material, sin mensualidades ni cargos ocultos. Incluye también todas las actualizaciones futuras sin costo adicional.',
  category: 'Compra'
}, {
  q: '¿Qué métodos de pago aceptan?',
  a: 'Aceptamos tarjetas de crédito y débito (Visa, Mastercard, American Express), PayPal y transferencias locales según tu país. El pago es 100% seguro y encriptado.',
  category: 'Compra'
}, {
  q: '¿El acceso es de por vida? ¿Recibo actualizaciones?',
  a: 'Sí, el acceso es permanente. Además, recibirás todas las actualizaciones futuras del MegaPack sin costo adicional, para siempre.',
  category: 'Compra'
}, {
  q: '¿En qué dispositivos puedo usarlo?',
  a: 'Todos los recursos están en formato PDF y digital, compatibles con cualquier dispositivo: computadora, tablet o teléfono. Puedes imprimir las sesiones o usarlas directamente en el borde de la piscina.',
  category: 'Uso'
}, {
  q: '¿Necesito experiencia previa para aprovecharlo?',
  a: 'No. El material está diseñado para servir tanto a profesores principiantes como a entrenadores con años de experiencia. Cada sesión incluye instrucciones paso a paso y consejos pedagógicos.',
  category: 'Uso'
}, {
  q: '¿Cómo funciona la garantía de 7 días?',
  a: 'Tienes 7 días desde tu compra para revisar todo el material. Si no te convence por cualquier motivo, te devolvemos el 100% de tu dinero sin preguntas. Solo escríbenos un email.',
  category: 'Garantía'
}];
const categoryStyles: Record<Category, string> = {
  Compra: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Producto: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  Uso: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Garantía: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
};
const filters: ('Todas' | Category)[] = ['Todas', 'Compra', 'Producto', 'Uso', 'Garantía'];
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<'Todas' | Category>('Todas');
  const filtered = faqs.map((f, i) => ({
    ...f,
    originalIndex: i
  })).filter(f => activeFilter === 'Todas' || f.category === activeFilter);
  return <section className="w-full bg-bg-main py-24 px-4 relative overflow-hidden" data-id="element-225">
      {/* Decorative blobs */}
      <div aria-hidden className="absolute top-1/4 -right-20 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-226" />
      <div aria-hidden className="absolute bottom-1/4 -left-20 w-[400px] h-[400px] bg-cta-green/5 rounded-full blur-3xl pointer-events-none" data-id="element-227" />

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-228">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="text-center mb-10" data-id="element-229">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6" data-id="element-230">
            <HelpCircleIcon className="w-4 h-4 text-primary" data-id="element-231" />
            <span className="text-sm font-semibold text-primary tracking-wide uppercase" data-id="element-232">
              Resolvemos tus dudas
            </span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-4 tracking-tight" data-id="element-233">
            Preguntas Frecuentes
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-xl mx-auto" data-id="element-234">
            Todo lo que necesitas saber antes de empezar.{' '}
            <span className="text-text-main font-semibold" data-id="element-235">
              ¿No la encuentras?
            </span>{' '}
            Escríbenos.
          </p>
        </motion.div>

        {/* Category Filters */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="flex flex-wrap items-center justify-center gap-2 mb-10" role="tablist" aria-label="Filtrar preguntas por categoría" data-id="element-236">
          {filters.map(f => {
          const isActive = activeFilter === f;
          return <button key={f} role="tab" aria-selected={isActive} onClick={() => {
            setActiveFilter(f);
            setOpenIndex(null);
          }} className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold border transition-all duration-200 ${isActive ? 'bg-primary text-white border-primary shadow-[0_0_20px_rgba(59,130,246,0.4)]' : 'bg-bg-alt text-text-sec border-border-light hover:border-white/20 hover:text-text-main'}`} data-id="element-237">
                {f}
              </button>;
        })}
        </motion.div>

        {/* FAQ list */}
        <div className="space-y-3 mb-16" data-id="element-238">
          <AnimatePresence mode="popLayout" data-id="element-239">
            {filtered.map((faq, displayIdx) => {
            const isOpen = openIndex === faq.originalIndex;
            return <motion.div key={faq.originalIndex} layout initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3,
              delay: displayIdx * 0.03
            }} className={`bg-bg-alt border rounded-2xl overflow-hidden transition-colors duration-300 ${isOpen ? 'border-primary/50 shadow-[0_0_25px_rgba(59,130,246,0.12)]' : 'border-border-light hover:border-white/20'}`} data-id="element-240">
                  <button className="w-full text-left px-5 sm:px-6 py-5 flex items-center justify-between gap-4" onClick={() => setOpenIndex(isOpen ? null : faq.originalIndex)} aria-expanded={isOpen} data-id="element-241">
                    <div className="flex-1 min-w-0" data-id="element-242">
                      <div className="flex items-center gap-2 mb-1.5" data-id="element-243">
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${categoryStyles[faq.category]}`} data-id="element-244">
                          {faq.category}
                        </span>
                      </div>
                      <span className={`block font-bold text-base sm:text-lg leading-snug transition-colors ${isOpen ? 'text-primary' : 'text-text-main'}`} data-id="element-245">
                        {faq.q}
                      </span>
                    </div>
                    <div className={`w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center border transition-all duration-300 ${isOpen ? 'bg-primary/15 border-primary/40 text-primary rotate-45' : 'bg-bg-dark/40 border-border-light text-text-sec'}`} data-id="element-246">
                      <PlusIcon className="w-4 h-4" strokeWidth={2.5} data-id="element-247" />
                    </div>
                  </button>

                  <AnimatePresence initial={false} data-id="element-248">
                    {isOpen && <motion.div key="content" initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-249">
                        <div className="px-5 sm:px-6 pb-6 pt-0" data-id="element-250">
                          <div className="border-t border-border-light pt-4" data-id="element-251">
                            <p className="text-text-sec text-base leading-relaxed" data-id="element-252">
                              {faq.a}
                            </p>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </AnimatePresence>
        </div>

        {/* Still have questions? */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-bg-alt border border-border-light rounded-2xl p-5 sm:p-6 mb-12 flex flex-col sm:flex-row items-center gap-4 sm:gap-5 text-center sm:text-left" data-id="element-253">
          <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0" data-id="element-254">
            <div className="w-6 h-6 text-primary" data-id="element-255" />
          </div>
          <div className="flex-1" data-id="element-256">
            <p className="text-text-main font-bold text-base sm:text-lg leading-tight mb-0.5" data-id="element-257">
              ¿Sigues con dudas?
            </p>
            <p className="text-text-sec text-sm" data-id="element-258">
              Escríbenos y te respondemos en menos de 24 horas.
            </p>
          </div>
          <a href="mailto:soporte@edulabs.online" className="inline-flex items-center gap-2 bg-bg-dark border border-border-light hover:border-primary/50 text-text-main font-semibold text-sm px-4 py-2.5 rounded-xl transition-all hover:text-primary" data-id="element-259">
            <MailIcon className="w-4 h-4" data-id="element-260" />
            Contactar soporte
          </a>
        </motion.div>

        {/* Final CTA */}
        <div className="text-center relative" data-id="element-261">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-cta-green/20 blur-[80px] pointer-events-none rounded-full" data-id="element-262"></div>
          <a href="#pricing" className="relative btn-primary-pulse bg-cta-green font-black text-lg sm:text-2xl px-8 sm:px-12 py-4 sm:py-5 rounded-2xl hover:bg-cta-green-hover transition-all inline-block mb-4 w-full sm:w-auto max-w-2xl text-white shadow-[0_0_30px_rgba(16,185,129,0.3)] border border-white/20" data-id="element-263">
            🚀 ¡SÍ! QUIERO RECUPERAR MI TIEMPO
          </a>
          <p className="text-text-sec text-sm font-medium flex items-center justify-center gap-2" data-id="element-264">
            <span data-id="element-265">⚡ Acceso Inmediato</span>
            <span data-id="element-266">•</span>
            <span data-id="element-267">🛡️ Garantía de 7 Días</span>
          </p>
        </div>
      </div>
    </section>;
}